import api from './api';
import {
  ApiResponse,
  DetectionDataset,
  DetectionFlag,
  DetectionObject,
  DetectionObjectType,
  DetectionSearchPayload,
  DetectionSearchResponse,
} from '../types';

// Re-export types for convenience
export type { DetectionSearchPayload, DetectionSearchResponse };

// Patch payload type (uses {id} wrapper for reference fields)
export interface DetectionPatchPayload {
  dataset?: { id: string };
  flags?: { id: string }[];
}

// Object create payload
export interface DetectionObjectCreatePayload {
  image: string;
  box: [number, number, number, number];
  type: DetectionObjectType;
  angle?: number | null;
}

// Object patch payload
export interface DetectionObjectPatchPayload {
  box?: [number, number, number, number];
  type?: DetectionObjectType;
  angle?: number | null;
}

// Dropdown data response type
export interface DetectionDropdownData {
  datasets: DetectionDataset[];
  flags: DetectionFlag[];
}

/**
 * Fetches all dropdown data for detection search/edit forms.
 */
export async function fetchDetectionDropdownData(signal?: AbortSignal): Promise<DetectionDropdownData> {
  const config = signal ? { signal } : {};

  const [datasetsRes, flagsRes] = await Promise.all([
    api.get<ApiResponse<DetectionDataset[]>>('datasets/list/detection', config),
    api.get<ApiResponse<DetectionFlag[]>>('detection/list/flags', config),
  ]);

  return {
    datasets: datasetsRes.data.results || [],
    flags: flagsRes.data.results || [],
  };
}

/**
 * Searches for detections using the given payload.
 */
export async function searchDetections(
  payload: DetectionSearchPayload,
  signal?: AbortSignal
): Promise<DetectionSearchResponse> {
  const config = signal ? { signal } : {};
  const response = await api.post<ApiResponse<DetectionSearchResponse>>('detection/search', payload, config);
  const data = response.data.results;

  return {
    detections: data.items || [],
    totalResults: data.totalResults || 0,
    page: data.page || 1,
    pageSize: data.pageSize || 20,
  };
}

/**
 * Updates a detection with the given patch data.
 */
export async function patchDetection(detectionId: string, patch: DetectionPatchPayload): Promise<void> {
  await api.patch(`detection/patch/${detectionId}`, patch);
}

/**
 * Updates multiple detections with the same patch data.
 * Uses Promise.allSettled for parallel requests.
 */
export async function patchDetections(
  detectionIds: string[],
  patch: DetectionPatchPayload
): Promise<{ succeeded: string[]; failed: string[] }> {
  const results = await Promise.allSettled(
    detectionIds.map((id) => api.patch(`detection/patch/${id}`, patch))
  );

  const succeeded: string[] = [];
  const failed: string[] = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      succeeded.push(detectionIds[index]);
    } else {
      failed.push(detectionIds[index]);
    }
  });

  return { succeeded, failed };
}

/**
 * Creates a new object on a detection.
 */
export async function createObject(payload: DetectionObjectCreatePayload): Promise<DetectionObject> {
  const response = await api.post<ApiResponse<DetectionObject>>('detection/object/create', payload);
  return response.data.results;
}

/**
 * Updates an object with the given patch data.
 */
export async function patchObject(objectId: string, patch: DetectionObjectPatchPayload): Promise<void> {
  await api.patch(`detection/object/patch/${objectId}`, patch);
}

/**
 * Deletes an object.
 */
export async function deleteObject(objectId: string): Promise<void> {
  await api.delete(`detection/object/delete/${objectId}`);
}

/**
 * Deletes a single detection.
 * Admin only.
 */
export async function deleteDetection(detectionId: string): Promise<void> {
  await api.delete(`detection/delete/${detectionId}`);
}

/**
 * Deletes multiple detections.
 * Uses Promise.allSettled for parallel requests.
 * Admin only.
 */
export async function deleteDetections(
  detectionIds: string[]
): Promise<{ succeeded: string[]; failed: string[] }> {
  const results = await Promise.allSettled(
    detectionIds.map((id) => api.delete(`detection/delete/${id}`))
  );

  const succeeded: string[] = [];
  const failed: string[] = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      succeeded.push(detectionIds[index]);
    } else {
      failed.push(detectionIds[index]);
    }
  });

  return { succeeded, failed };
}
